import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Link, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  CloseRounded as CloseIcon,
  DeleteOutline as DeleteIcon,
  InsertDriveFileOutlined as FileIcon,
  PaymentsOutlined as RepayIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../../../utils/functions/api';
import { confirmAsync } from '../../../../utils/functions/globalDialog';
import { formatCurrency } from '../../utils/format';
import DebtRepaymentDialog from './DebtRepaymentDialog';

export const getRepaid = (note) => (note?.repayments || []).reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
// คงเหลือ = ยอดหนี้ − คืนแล้ว (null ถ้าไม่ได้ระบุยอดหนี้ไว้)
export const getRemaining = (note) => (note?.amount == null ? null : Number(note.amount) - getRepaid(note));
// คงเหลือรวมของหลายรายการ (นับเฉพาะรายการที่ยังค้าง ไม่ให้รายการคืนเกินมาหักล้างรายการอื่น)
export const sumRemaining = (notes) => (notes || []).reduce((sum, r) => sum + Math.max(getRemaining(r) || 0, 0), 0);

const NUM_SX = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
const HEAD_SX = { py: 1, fontSize: '0.8rem', fontWeight: 600, color: '#64748b', bgcolor: '#f8fafc', borderColor: '#e2e8f0' };
const CELL_SX = { py: 1.25, fontSize: '0.9rem', borderColor: '#f1f5f9', verticalAlign: 'top' };

function Stat({ label, value, color }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, px: 2.5, py: 1.75, '& + &': { borderLeft: '1px solid #e2e8f0' } }}>
      <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ ...NUM_SX, fontSize: '1.35rem', fontWeight: 700, color: color || '#0f172a' }}>{value}</Typography>
    </Box>
  );
}

// รายละเอียดรายการเจ้าหนี้/ลูกหนี้ + log การคืนเงินทั้งหมด (ล่าสุดก่อน) + เปิดไฟล์หลักฐานได้ — โหลดข้อมูลสดจาก
// GET /debt-note/:id ทุกครั้งที่เปิด/มีการเปลี่ยนแปลง แล้วเรียก onChanged ให้ตารางรายการรีเฟรชตาม
export default function DebtNoteDetailDialog({ noteId, title, onClose, onChanged }) {
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repayOpen, setRepayOpen] = useState(false);

  const load = useCallback(async () => {
    if (!noteId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/debt-note/${noteId}`);
      setNote(data);
    } finally {
      setLoading(false);
    }
  }, [noteId]);

  useEffect(() => {
    setNote(null);
    load();
  }, [load]);

  const handleDeleteRepayment = async (r) => {
    const ok = await confirmAsync(`ลบรายการคืนเงิน ${formatCurrency(r.amount)} วันที่ ${dayjs(r.date).format('DD/MM/YYYY')}?`);
    if (!ok) return;
    await api.delete(`${process.env.REACT_APP_API_URL}/debt-note/${noteId}/repayments/${r._id}`);
    load();
    onChanged();
  };

  const repaid = getRepaid(note);
  const remaining = getRemaining(note);
  const logs = [...(note?.repayments || [])].sort(
    (a, b) => new Date(b.date) - new Date(a.date) || new Date(b.createdAt) - new Date(a.createdAt),
  );
  let remainingColor = '#cbd5e1';
  if (remaining != null) remainingColor = remaining > 0 ? '#dc2626' : '#16a34a';

  return (
    <Dialog open={!!noteId} onClose={onClose} fullWidth maxWidth="md">
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, py: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>{title}</Typography>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.3 }} noWrap>{note?.party || '…'}</Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          disableElevation
          startIcon={<RepayIcon />}
          onClick={() => setRepayOpen(true)}
          disabled={!note}
        >
          บันทึกการคืน
        </Button>
        <IconButton onClick={onClose} aria-label="ปิด"><CloseIcon /></IconButton>
      </Stack>

      <DialogContent sx={{ p: 3 }}>
        {loading && !note ? (
          <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
        ) : note && (
          <>
            <Box sx={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 2, mb: 2 }}>
              <Stat label="ยอดหนี้" value={note.amount == null ? '–' : formatCurrency(note.amount)} />
              <Stat label="คืนแล้ว" value={formatCurrency(repaid)} color="#16a34a" />
              <Stat
                label={remaining != null && remaining < 0 ? 'คืนเกิน' : 'คงเหลือ'}
                value={remaining == null ? '–' : formatCurrency(Math.abs(remaining))}
                color={remaining != null && remaining < 0 ? '#ea580c' : remainingColor}
              />
            </Box>

            {note.note && (
              <Typography sx={{ fontSize: '0.95rem', whiteSpace: 'pre-line', mb: 0.75 }}>{note.note}</Typography>
            )}
            <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mb: 3 }}>
              บันทึกโดย {note.created_by_name || '-'} · {dayjs(note.createdAt).format('DD/MM/YYYY HH:mm')}
            </Typography>

            <Typography sx={{ fontSize: '1rem', fontWeight: 700, mb: 1 }}>
              ประวัติการคืนเงิน
              <Box component="span" sx={{ ml: 1, fontWeight: 400, fontSize: '0.85rem', color: '#94a3b8' }}>{logs.length} ครั้ง</Box>
            </Typography>
            <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_SX}>วันที่คืน</TableCell>
                    <TableCell sx={HEAD_SX} align="right">จำนวนเงิน</TableCell>
                    <TableCell sx={HEAD_SX}>หมายเหตุ</TableCell>
                    <TableCell sx={HEAD_SX}>หลักฐาน</TableCell>
                    <TableCell sx={HEAD_SX}>บันทึกโดย</TableCell>
                    <TableCell sx={{ ...HEAD_SX, width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ ...CELL_SX, py: 4, textAlign: 'center', color: '#94a3b8' }}>
                        ยังไม่มีการคืนเงิน
                      </TableCell>
                    </TableRow>
                  )}
                  {logs.map((r) => (
                    <TableRow key={r._id} hover>
                      <TableCell sx={{ ...CELL_SX, ...NUM_SX }}>{dayjs(r.date).format('DD/MM/YYYY')}</TableCell>
                      <TableCell sx={{ ...CELL_SX, ...NUM_SX, fontWeight: 700, color: '#16a34a' }} align="right">
                        {formatCurrency(r.amount)}
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: 'pre-line', color: r.note ? undefined : '#cbd5e1' }}>
                        {r.note || '–'}
                      </TableCell>
                      <TableCell sx={CELL_SX}>
                        {(r.files || []).length === 0 ? <Box sx={{ color: '#cbd5e1' }}>–</Box> : (
                          <Stack spacing={0.75}>
                            {r.files.map((f) => (
                              <Link
                                key={f.url}
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="hover"
                                title={f.name}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem', maxWidth: 240 }}
                              >
                                <FileIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {f.name || 'ไฟล์หลักฐาน'}
                                </Box>
                              </Link>
                            ))}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, whiteSpace: 'nowrap', fontSize: '0.85rem', color: '#64748b' }}>
                        {r.created_by_name || '-'}
                        <Box sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{dayjs(r.createdAt).format('DD/MM/YY HH:mm')}</Box>
                      </TableCell>
                      <TableCell sx={{ ...CELL_SX, py: 0.75 }} align="center">
                        <Tooltip title="ลบรายการคืนนี้">
                          <IconButton onClick={() => handleDeleteRepayment(r)} sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626' } }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>

      <DebtRepaymentDialog
        note={repayOpen ? note : null}
        remaining={remaining}
        onClose={() => setRepayOpen(false)}
        onSaved={() => { setRepayOpen(false); load(); onChanged(); }}
      />
    </Dialog>
  );
}
